/**
 * GlassSpinner — the site-wide loading indicator.
 *
 * A drop-in replacement for MUI's CircularProgress (same `size`, `thickness`,
 * `color`, `sx`, `className` props) rendered as a small piece of glass: a
 * frosted disc with a conic brand-tinted arc turning around a breathing core.
 * Pure CSS animation, so hundreds of instances across the app cost nothing.
 *
 * Colours follow the tenant via --brand-primary / --brand-secondary (set by
 * ThemeContext) and the light/dark register via --surface / --hairline.
 */
import { Box, SxProps, Theme } from '@mui/material';

export interface GlassSpinnerProps {
  /** Outer diameter in px (or any CSS length). Default 40. */
  size?: number | string;
  /** Ring thickness as a fraction of the MUI scale (default 3.6). */
  thickness?: number;
  /** 'inherit' paints the arc with currentColor; anything else uses brand vars. */
  color?: 'inherit' | 'primary' | 'secondary' | (string & {});
  sx?: SxProps<Theme>;
  className?: string;
  'aria-label'?: string;
}

const KEYFRAMES = {
  '@keyframes mmGlassSpin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  '@keyframes mmGlassBreathe': {
    '0%, 100%': { transform: 'scale(0.82)', opacity: 0.55 },
    '50%': { transform: 'scale(1.08)', opacity: 1 },
  },
  '@keyframes mmGlassPulse': {
    '0%, 100%': { opacity: 0.35 },
    '50%': { opacity: 1 },
  },
} as const;

export function GlassSpinner({
  size = 40,
  thickness = 3.6,
  color = 'primary',
  sx,
  className,
  'aria-label': ariaLabel = 'Loading',
}: GlassSpinnerProps) {
  const px = typeof size === 'number' ? size : parseFloat(String(size)) || 40;
  const isPx = typeof size === 'number' || /px$|^\d+(\.\d+)?$/.test(String(size));
  const dim = isPx ? `${px}px` : size;

  // Ring width scales with the diameter, floored so it survives 14px buttons.
  const ring = Math.max(1.6, (px * thickness) / 44);
  const inset = Math.max(1.5, px * 0.09);
  const core = Math.max(2.5, px * 0.16);

  // `color="inherit"` or an explicit `sx={{ color }}` (the old CircularProgress
  // idiom for spinners inside coloured buttons) both paint with currentColor.
  const sxHasColor = !!sx && typeof sx === 'object' && !Array.isArray(sx) && 'color' in (sx as object);
  const inherit = color === 'inherit' || sxHasColor;
  const primary = inherit ? 'currentColor' : 'var(--brand-primary, #2D5BE3)';
  const secondary = inherit ? 'currentColor' : 'var(--brand-secondary, #8B5CF6)';
  const arc = `conic-gradient(from 0deg, transparent 0deg, transparent 150deg, ${secondary} 245deg, ${primary} 350deg, transparent 360deg)`;
  const mask = `radial-gradient(farthest-side, transparent calc(100% - ${ring}px), #000 calc(100% - ${ring}px + 0.6px))`;
  // The "glow" is a wider, fainter copy of the arc — no filter: blur(), so a
  // spinning layer never has to be re-filtered per frame.
  const glowRing = ring * 2.6;
  const glowInset = Math.max(0, inset - ring * 0.8);
  const glowMask = `radial-gradient(farthest-side, transparent calc(100% - ${glowRing}px), #000 calc(100% - ${glowRing}px + 0.6px))`;

  return (
    <Box
      role="progressbar"
      aria-label={ariaLabel}
      aria-busy="true"
      className={className}
      sx={{
        position: 'relative',
        display: 'inline-block',
        flexShrink: 0,
        width: dim,
        height: dim,
        verticalAlign: 'middle',
        ...KEYFRAMES,
        // frosted disc
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: inherit
            ? 'color-mix(in srgb, currentColor 8%, transparent)'
            : 'color-mix(in srgb, var(--surface, #FFFFFF) 55%, transparent)',
          border: `1px solid ${inherit ? 'color-mix(in srgb, currentColor 18%, transparent)' : 'color-mix(in srgb, var(--hairline, #E2E8F0) 75%, transparent)'}`,
          boxShadow: inherit
            ? 'none'
            : 'inset 0 1px 0 rgba(255,255,255,0.55), 0 6px 18px rgba(15,23,42,0.08)',
          // No backdrop-filter: a page can show dozens of these at once and
          // each one would re-blur its backdrop whenever anything behind moved.
        },
        // luminous halo under the arc
        '& [data-gs-glow]': {
          position: 'absolute',
          inset: glowInset,
          borderRadius: '50%',
          background: arc,
          WebkitMask: glowMask,
          mask: glowMask,
          opacity: 0.22,
          animation: 'mmGlassSpin 1.15s cubic-bezier(0.45, 0.05, 0.35, 0.95) infinite',
          willChange: 'transform',
        },
        // the arc itself
        '& [data-gs-arc]': {
          position: 'absolute',
          inset,
          borderRadius: '50%',
          background: arc,
          WebkitMask: mask,
          mask,
          animation: 'mmGlassSpin 1.15s cubic-bezier(0.45, 0.05, 0.35, 0.95) infinite',
          willChange: 'transform',
        },
        // breathing core
        '& [data-gs-core]': {
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: core,
          height: core,
          marginTop: -core / 2,
          marginLeft: -core / 2,
          borderRadius: '50%',
          background: primary,
          boxShadow: inherit ? 'none' : `0 0 ${Math.max(4, px * 0.18)}px ${primary}`,
          animation: 'mmGlassBreathe 1.6s ease-in-out infinite',
        },
        '@media (prefers-reduced-motion: reduce)': {
          '& [data-gs-glow], & [data-gs-arc]': { animation: 'mmGlassPulse 1.6s ease-in-out infinite' },
          '& [data-gs-core]': { animation: 'none', opacity: 0.8 },
        },
        ...(sx as object),
      }}
    >
      <Box data-gs-glow aria-hidden />
      <Box data-gs-arc aria-hidden />
      <Box data-gs-core aria-hidden />
    </Box>
  );
}

export default GlassSpinner;
